import { useState, useEffect, useCallback } from 'react';
import { fetchBroadcasts, fetchUserDatasets, fetchPricingModels, savePricingOverride, deletePricingOverride, setAccountPricing, hideBroadcasts, unhideBroadcasts, fetchCostCaps, createCostCap, updateCostCap, deleteCostCap, BroadcastResponse, PricingModelOptionResponse, PricingInfo, CostCapInput } from '../lib/api';
import { Broadcast, CostCap, DashboardFilters, SummaryStats, PricingModelOption } from '../types';
import { useAuth } from '../context/AuthContext';
import { calculateCostClientSide, formatModelLabel } from '../utils/pricing';
import { formatDate } from '../utils/formatters';

export const useBroadcasts = () => {
  const { isAdmin, setIsAdmin, isSuperAdmin, setIsSuperAdmin } = useAuth();
  const [broadcasts, setBroadcasts] = useState<Broadcast[]>([]);
  const [accounts, setAccounts] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pricingModelOptions, setPricingModelOptions] = useState<PricingModelOption[]>([]);
  const [pricingInfoMap, setPricingInfoMap] = useState<Record<string, PricingInfo>>({});
  const [costCaps, setCostCaps] = useState<CostCap[]>([]);
  const [costCapsError, setCostCapsError] = useState<string | null>(null);
  const [filters, setFilters] = useState<DashboardFilters>({
    account: '',
    startDate: '',
    endDate: ''
  });

  // Transform API response to match Broadcast type
  const transformBroadcast = (b: BroadcastResponse): Broadcast => ({
    id: `${b.account}-${b.broadcast_date_id}`,
    account: b.account,
    broadcast_date_id: b.broadcast_date_id,
    created_at: b.created_at || '',
    modified_at: b.modified_at || undefined,
    delivered: b.delivered,
    donations: b.donations,
    amount_raised: b.amount_raised,
    client_cost: b.client_cost,
    client_roi: b.client_roi,
    client_roi_percentage: b.client_roi_percentage,
    pricing_model: b.pricing_model || 'fixed_cost',
    sb_cost: b.sb_cost,
    model_type: b.model_type,
    model_value: b.model_value,
    hidden: b.hidden,
    uncapped_cost: b.uncapped_cost,
    cost_cap_ids: b.cost_cap_ids,
  });

  // Cost caps for the selected account (admin-only). The list endpoint re-runs
  // the broadcast pipeline to work out used/left, so it loads alongside the
  // table rather than blocking it.
  const loadCostCaps = useCallback(async () => {
    if (!isAdmin || !filters.account || filters.account === 'all') {
      setCostCaps([]);
      return;
    }
    try {
      setCostCaps(await fetchCostCaps(filters.account));
      setCostCapsError(null);
    } catch (err) {
      setCostCapsError(err instanceof Error ? err.message : 'Failed to load cost caps');
    }
  }, [isAdmin, filters.account]);

  const loadBroadcasts = useCallback(async () => {
    if (!filters.account) {
      setLoading(false);
      setBroadcasts([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await fetchBroadcasts({
        account: filters.account,
        start_date: filters.startDate,
        end_date: filters.endDate
      });

      const transformed = data.map(transformBroadcast);
      setBroadcasts(transformed);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch broadcasts');
      setBroadcasts([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Fetch user's allowed datasets + admin status
  const loadUserDatasets = useCallback(async () => {
    try {
      const { datasets, is_admin, is_superadmin, pricing_info } = await fetchUserDatasets();
      const sortedDatasets = datasets.sort();
      setAccounts(sortedDatasets);
      setIsAdmin(is_admin);
      setIsSuperAdmin(is_superadmin);
      if (pricing_info) setPricingInfoMap(pricing_info);

      if (sortedDatasets.length > 0) {
        const params = new URLSearchParams(window.location.search);
        const savedAccount = params.get('account');
        const initial = savedAccount && sortedDatasets.includes(savedAccount)
          ? savedAccount
          : sortedDatasets[0];
        setFilters(prev => ({ ...prev, account: initial }));
      }

      // Load pricing model options if admin
      if (is_admin) {
        try {
          const models = await fetchPricingModels();
          setPricingModelOptions(models.map((m: PricingModelOptionResponse) => ({
            model_type: m.model_type,
            default_value: m.default_value,
            label: m.label,
          })));
        } catch (err) {
          console.error('Failed to load pricing models:', err);
        }
      }
    } catch (err) {
      console.error('Failed to fetch user datasets:', err);
    }
  }, [setIsAdmin, setIsSuperAdmin]);

  useEffect(() => {
    loadUserDatasets();
  }, [loadUserDatasets]);

  useEffect(() => {
    loadBroadcasts();
  }, [loadBroadcasts]);

  useEffect(() => {
    loadCostCaps();
  }, [loadCostCaps]);

  // Sync selected account to URL so it survives refresh
  useEffect(() => {
    if (!filters.account) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('account') !== filters.account) {
      params.set('account', filters.account);
      window.history.replaceState({}, '', `${window.location.pathname}?${params}`);
    }
  }, [filters.account]);

  // Client-side only recalculation (preview, not persisted).
  // Applies to every broadcast on the same calendar day, same account.
  const handlePricingModelChange = useCallback((
    broadcastId: string,
    modelType: string,
    modelValue: number
  ) => {
    setBroadcasts(prev => {
      const target = prev.find(b => b.id === broadcastId);
      if (!target) return prev;
      const targetDate = formatDate(target.created_at);
      const targetAccount = target.account;

      const sameDay = (b: Broadcast) =>
        b.account === targetAccount && formatDate(b.created_at) === targetDate;

      // Profit share must be computed on the DAY's totals (so a money-losing
      // send is netted against the winners, not forgiven on its own), then
      // split back across the day's sends. This mirrors the backend's day-level
      // re-clamp. Other models are linear, so per-broadcast is already correct.
      let shares: Record<string, number> | null = null;
      if (modelType === 'profit_share') {
        const dayRows = prev.filter(sameDay);
        const dayRaised = dayRows.reduce((s, b) => s + (b.amount_raised || 0), 0);
        const daySb = dayRows.reduce((s, b) => s + (b.sb_cost || 0), 0);
        const dayDelivered = dayRows.reduce((s, b) => s + (b.delivered || 0), 0);
        const dayCost = calculateCostClientSide(modelType, modelValue, dayRaised, daySb, dayDelivered);
        shares = {};
        let assigned = 0;
        dayRows.forEach((b, i) => {
          const share = i === dayRows.length - 1
            ? Math.round((dayCost - assigned) * 100) / 100
            : daySb > 0
              ? Math.round(dayCost * ((b.sb_cost || 0) / daySb) * 100) / 100
              : Math.round((dayCost / dayRows.length) * 100) / 100;
          assigned += share;
          shares![b.id] = share;
        });
      }

      return prev.map(b => {
        if (!sameDay(b)) return b;

        const rawCost = shares
          ? shares[b.id]
          : calculateCostClientSide(modelType, modelValue, b.amount_raised, b.sb_cost || 0, b.delivered);
        const newCost = Math.round(rawCost * 100) / 100;
        const newRoi = b.amount_raised - newCost;
        const newRoiPct = b.amount_raised === 0
          ? 0
          : modelType === 'roi_guarantee' && modelValue > 0
            ? modelValue
            : (newCost > 0 ? (b.amount_raised / newCost) * 100 : 0);

        return {
          ...b,
          model_type: modelType,
          model_value: modelValue,
          client_cost: newCost,
          client_roi: Math.round(newRoi * 100) / 100,
          client_roi_percentage: Math.round(newRoiPct * 100) / 100,
          pricing_model: formatModelLabel(modelType, modelValue),
        };
      });
    });
  }, []);

  // Persist current local pricing model to the backend — fan out across every
  // broadcast on the same calendar day so the override covers the whole row.
  // Sequential to avoid hammering Supabase's admin check under concurrent load.
  const savePricingModel = useCallback(async (broadcastId: string) => {
    const broadcast = broadcasts.find(b => b.id === broadcastId);
    if (!broadcast || !broadcast.model_type || !broadcast.account) return;

    const dayKey = formatDate(broadcast.created_at);
    const siblings = broadcasts.filter(b =>
      b.account === broadcast.account &&
      formatDate(b.created_at) === dayKey &&
      b.broadcast_date_id
    );

    for (const b of siblings) {
      await savePricingOverride({
        account: b.account,
        broadcast_date_id: b.broadcast_date_id,
        model_type: broadcast.model_type!,
        model_value: broadcast.model_value ?? 0,
      });
    }

    // The local preview can't redo cap budgets, so a day inside a cap reloads
    // to pick up the server's capped cost (and the caps' new used/left).
    if (siblings.some(b => b.cost_cap_ids?.length)) {
      await loadBroadcasts();
      await loadCostCaps();
    }
  }, [broadcasts, loadBroadcasts, loadCostCaps]);

  // Remove overrides for every broadcast on the same calendar day and reload.
  const resetPricingModel = useCallback(async (broadcastId: string) => {
    const broadcast = broadcasts.find(b => b.id === broadcastId);
    if (!broadcast || !broadcast.account) return;

    const dayKey = formatDate(broadcast.created_at);
    const siblings = broadcasts.filter(b =>
      b.account === broadcast.account &&
      formatDate(b.created_at) === dayKey &&
      b.broadcast_date_id
    );

    for (const b of siblings) {
      await deletePricingOverride({
        account: b.account,
        broadcast_date_id: b.broadcast_date_id,
      });
    }
    await loadBroadcasts();
  }, [broadcasts, loadBroadcasts]);

  const calculateStats = useCallback((): SummaryStats => {
    // Hidden day-rows are excluded from every total so the summary matches the
    // client-facing view (admins see hidden rows greyed in the table, but they
    // never count toward the numbers).
    const visible = broadcasts.filter(b => !b.hidden);

    if (visible.length === 0) {
      return {
        totalBroadcasts: 0,
        totalDelivered: 0,
        totalDonations: 0,
        totalRaised: 0,
        totalCost: 0,
        totalRoi: 0,
        roiPercentage: 0,
        allRowsGreen: true
      };
    }

    const totalDelivered = visible.reduce((sum, b) => sum + (b.delivered || 0), 0);
    const totalDonations = visible.reduce((sum, b) => sum + (b.donations || 0), 0);
    const totalRaised = visible.reduce((sum, b) => sum + (b.amount_raised || 0), 0);
    const totalCost = visible.reduce((sum, b) => sum + (b.client_cost || 0), 0);
    const totalRoi = totalRaised - totalCost;
    const roiPercentage = totalCost > 0 ? (totalRaised / totalCost) * 100 : 0;

    // Aggregate by display date (same grouping as DataTable) then check each day-row
    const dayMap = new Map<string, { raised: number; cost: number; model_type?: string; model_value?: number }>();
    for (const b of visible) {
      const dayKey = formatDate(b.created_at);
      const existing = dayMap.get(dayKey);
      if (existing) {
        existing.raised += b.amount_raised || 0;
        existing.cost += b.client_cost || 0;
      } else {
        dayMap.set(dayKey, {
          raised: b.amount_raised || 0,
          cost: b.client_cost || 0,
          model_type: b.model_type,
          model_value: b.model_value,
        });
      }
    }
    const allRowsGreen = Array.from(dayMap.values()).every(row => {
      const threshold = row.model_type === 'roi_guarantee' && row.model_value && row.model_value > 0
        ? row.model_value : 100;
      const roi = row.cost > 0 ? (row.raised / row.cost) * 100 : threshold;
      return Math.round(roi * 100) / 100 >= threshold;
    });

    return {
      totalBroadcasts: visible.length,
      totalDelivered,
      totalDonations,
      totalRaised,
      totalCost,
      totalRoi,
      roiPercentage,
      allRowsGreen
    };
  }, [broadcasts]);

  const clearFilters = useCallback(() => {
    setFilters(prev => ({
      account: prev.account,
      startDate: '',
      endDate: ''
    }));
  }, []);

  // Always use account-level pricing from PRICING_MODELS (not per-broadcast overrides)
  const accountPricingModel = filters.account && pricingInfoMap[filters.account]
    ? { label: pricingInfoMap[filters.account].label, model_type: pricingInfoMap[filters.account].model_type, model_value: pricingInfoMap[filters.account].model_value }
    : null;

  // Change pricing model for ALL broadcasts at once (account-level preview)
  const handleAccountPricingModelChange = useCallback((modelType: string, modelValue: number) => {
    broadcasts.forEach(b => {
      handlePricingModelChange(b.id, modelType, modelValue);
    });
  }, [broadcasts, handlePricingModelChange]);

  // Persist a new account-level pricing model. The backend closes out the
  // currently-active row and inserts a new one with effective_from=now(), so
  // past broadcasts continue to resolve to the old model and only future
  // broadcasts pick up the new one.
  const saveAccountPricingModel = useCallback(async (
    modelType: string,
    modelValue: number,
  ) => {
    if (!filters.account) return;
    await setAccountPricing({
      account: filters.account,
      model_type: modelType,
      model_value: modelValue,
    });
    // Refresh both pricing_info (the card label) and broadcasts (resolved costs)
    await loadUserDatasets();
    await loadBroadcasts();
  }, [filters.account, loadUserDatasets, loadBroadcasts]);

  // Hide/unhide whole day-rows for the current view. Callers pass display dates
  // (the DataTable's row keys); we fan out to every broadcast_date_id on those
  // days, grouped by account, mirroring how pricing overrides fan out. Hidden
  // rows vanish for clients; admins keep seeing them (greyed) until unhidden.
  const collectDateIdsByAccount = useCallback((displayDates: string[]) => {
    const dateSet = new Set(displayDates);
    const byAccount = new Map<string, Set<string>>();
    broadcasts.forEach(b => {
      if (b.broadcast_date_id && dateSet.has(formatDate(b.created_at))) {
        if (!byAccount.has(b.account)) byAccount.set(b.account, new Set());
        byAccount.get(b.account)!.add(b.broadcast_date_id);
      }
    });
    return byAccount;
  }, [broadcasts]);

  const hideDays = useCallback(async (displayDates: string[]) => {
    const byAccount = collectDateIdsByAccount(displayDates);
    for (const [account, ids] of Array.from(byAccount.entries())) {
      await hideBroadcasts({ account, broadcast_date_ids: Array.from(ids) });
    }
    await loadBroadcasts();
  }, [collectDateIdsByAccount, loadBroadcasts]);

  const unhideDays = useCallback(async (displayDates: string[]) => {
    const byAccount = collectDateIdsByAccount(displayDates);
    for (const [account, ids] of Array.from(byAccount.entries())) {
      await unhideBroadcasts({ account, broadcast_date_ids: Array.from(ids) });
    }
    await loadBroadcasts();
  }, [collectDateIdsByAccount, loadBroadcasts]);

  // Cost cap add/edit/delete for the selected account. Each one reloads the
  // table (costs are capped server-side) and the caps list (used/left).
  const refreshAfterCapChange = useCallback(async () => {
    await Promise.all([loadBroadcasts(), loadCostCaps()]);
  }, [loadBroadcasts, loadCostCaps]);

  const addCostCap = useCallback(async (input: CostCapInput) => {
    if (!filters.account) return;
    await createCostCap({ ...input, account: filters.account });
    await refreshAfterCapChange();
  }, [filters.account, refreshAfterCapChange]);

  const editCostCap = useCallback(async (id: number, input: CostCapInput) => {
    await updateCostCap(id, input);
    await refreshAfterCapChange();
  }, [refreshAfterCapChange]);

  const removeCostCap = useCallback(async (id: number) => {
    await deleteCostCap(id);
    await refreshAfterCapChange();
  }, [refreshAfterCapChange]);

  // Compute total sb_cost for the superadmin-only cost-estimate card. Hidden
  // rows are excluded so it matches the client-facing totals.
  const totalSbCost = isSuperAdmin
    ? broadcasts.filter(b => !b.hidden).reduce((sum, b) => sum + (b.sb_cost || 0), 0)
    : undefined;

  return {
    broadcasts,
    accounts,
    loading,
    error,
    filters,
    setFilters,
    fetchBroadcasts: loadBroadcasts,
    clearFilters,
    stats: calculateStats(),
    isAdmin,
    isSuperAdmin,
    pricingModelOptions,
    handlePricingModelChange,
    savePricingModel,
    resetPricingModel,
    totalSbCost,
    accountPricingModel,
    handleAccountPricingModelChange,
    saveAccountPricingModel,
    hideDays,
    unhideDays,
    costCaps,
    costCapsError,
    addCostCap,
    editCostCap,
    removeCostCap,
  };
};
